import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { useToast } from '@/hooks/use-toast';

interface CreateTicketData {
  title: string;
  description: string;
  category_id?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  system_id?: string;
  company_id: string;
  attachments?: File[];
}

interface UpdateTicketData {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed' | 'paused';
  assigned_to?: string | null;
  paused_reason?: string | null;
}

export function useSupportTickets(companyId?: string, userId?: string) {
  return useQuery({
    queryKey: ['support-tickets', companyId, userId],
    queryFn: async () => {
      console.log('🎫 Fetching support tickets for companyId:', companyId, 'userId:', userId);
      
      let query = supabase
        .from('support_tickets')
        .select(`
          *,
          company:companies!support_tickets_company_id_fkey(name),
          system:systems!support_tickets_system_id_fkey(name),
          category:support_categories!support_tickets_category_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (companyId) {
        console.log('🎫 Filtering by company_id:', companyId);
        query = query.eq('company_id', companyId);
      } else {
        console.log('🎫 Fetching ALL tickets (admin mode)');
      }

      if (userId) {
        console.log('🎫 Filtering by user_id:', userId);
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching support tickets:', error);
        throw error;
      }

      if (!data) return [];

      // Enrich with user profiles (created_by and assigned_to)
      const profileIds = Array.from(
        new Set(
          (data as any[]).flatMap((t: any) => [t.user_id, t.assigned_to].filter(Boolean))
        )
      );

      let profilesMap: Record<string, any> = {};
      if (profileIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', profileIds);
        if (!profilesError && profiles) {
          profilesMap = Object.fromEntries(profiles.map((p: any) => [p.id, p]));
        } else if (profilesError) {
          console.warn('⚠️ Error enriching profiles:', profilesError);
        }
      }

      const enriched = (data as any[]).map((t: any) => ({
        ...t,
        user_profile: profilesMap[t.user_id] || null,
        assigned_user: t.assigned_to ? profilesMap[t.assigned_to] || null : null,
      }));
      
      console.log('✅ Support tickets fetched:', enriched.length, 'tickets');
      console.log('📋 Support tickets data:', enriched);
      return enriched;
    },
  });
}

export function useSupportTicket(ticketId: string) {
  return useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(`
          *,
          company:companies!support_tickets_company_id_fkey(name),
          system:systems!support_tickets_system_id_fkey(name),
          category:support_categories!support_tickets_category_id_fkey(name),
          support_ticket_messages(
            id,
            message,
            is_internal,
            created_at,
            user_id
          )
        `)
        .eq('id', ticketId)
        .order('created_at', { ascending: true, referencedTable: 'support_ticket_messages' })
        .maybeSingle();

      if (error) throw error;
      if (!data) return null as any;

      const ticket: any = data;

      // Collect all profile IDs we need
      const msgUserIds = (ticket.support_ticket_messages || []).map((m: any) => m.user_id).filter(Boolean);
      const profileIds = Array.from(new Set([ticket.user_id, ticket.assigned_to, ...msgUserIds].filter(Boolean)));

      let profilesMap: Record<string, any> = {};
      if (profileIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', profileIds);
        if (!profilesError && profiles) {
          profilesMap = Object.fromEntries(profiles.map((p: any) => [p.id, p]));
        }
      }

      const enrichedTicket = {
        ...ticket,
        user_profile: profilesMap[ticket.user_id] || null,
        assigned_user: ticket.assigned_to ? profilesMap[ticket.assigned_to] || null : null,
        messages: (ticket.support_ticket_messages || []).map((m: any) => ({
          ...m,
          sender: profilesMap[m.user_id] || null,
          sender_name: profilesMap[m.user_id]?.full_name || 'Usuário não encontrado',
        })),
        attachments: ticket.attachments || [],
      } as any;

      return enrichedTicket;
    },
    enabled: !!ticketId,
  });
}

export function useCreateSupportTicket() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateTicketData) => {
      if (!user) throw new Error('User not authenticated');

      // Handle file uploads first
      let attachmentMetadata: any[] = [];
      
      if (data.attachments && data.attachments.length > 0) {
        for (const file of data.attachments) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('support-attachments')
            .upload(fileName, file);

          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            throw new Error(`Erro ao fazer upload do arquivo: ${file.name}`);
          }

          attachmentMetadata.push({
            name: file.name,
            path: fileName,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
          });
        }
      }

      // Filter out empty strings and convert to undefined for UUID fields
      const cleanData = {
        title: data.title,
        description: data.description,
        category_id: data.category_id === '' ? undefined : data.category_id,
        priority: data.priority,
        system_id: data.system_id === '' || data.system_id === 'none' ? undefined : data.system_id,
        company_id: data.company_id,
        user_id: user.id,
        attachments: attachmentMetadata,
      };

      console.log('Creating ticket with data:', cleanData); // Debug log

      const { data: ticketData, error } = await supabase
        .from('support_tickets')
        .insert(cleanData)
        .select()
        .single();

      if (error) throw error;

      // Send email notification
      try {
        await supabase.functions.invoke('send-support-notification', {
          body: { ticketId: ticketData.id }
        });
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't throw error - ticket was created successfully
      }

      return ticketData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      toast({
        title: "Chamado criado",
        description: "Seu chamado foi criado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar chamado",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateSupportTicket(ticketId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateTicketData) => {
      // Buscar dados atuais do ticket para detectar mudanças
      const { data: currentTicket } = await supabase
        .from('support_tickets')
        .select('status, assigned_to, priority')
        .eq('id', ticketId)
        .single();

      const { error } = await supabase
        .from('support_tickets')
        .update(data)
        .eq('id', ticketId);

      if (error) throw error;

      // Criar notificações baseadas nas mudanças
      if (user && currentTicket) {
        try {
          if (data.status && data.status !== currentTicket.status) {
            await supabase.functions.invoke('create-ticket-notification', {
              body: {
                ticketId: ticketId,
                interactionType: 'status_change',
                actorUserId: user.id,
                data: {
                  statusChange: {
                    from: currentTicket.status,
                    to: data.status
                  }
                }
              }
            });
          }

          if (data.assigned_to && data.assigned_to !== currentTicket.assigned_to) {
            await supabase.functions.invoke('create-ticket-notification', {
              body: {
                ticketId: ticketId,
                interactionType: 'assignment',
                actorUserId: user.id,
                data: {
                  assignment: {
                    assignedTo: data.assigned_to,
                    assignedBy: user.id
                  }
                }
              }
            });
          }
        } catch (notificationError) {
          console.error('Error creating notifications:', notificationError);
          // Não falhar se a notificação falhar
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['system-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-notification-count'] });
      toast({
        title: "Chamado atualizado",
        description: "Chamado atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar chamado",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useAddTicketMessage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { ticket_id: string; message: string; is_internal?: boolean }) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('support_ticket_messages')
        .insert({
          ticket_id: data.ticket_id,
          message: data.message,
          is_internal: data.is_internal || false,
          user_id: user.id,
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      // Criar notificação para mensagem (apenas se não for mensagem interna)
      if (!variables.is_internal && user) {
        try {
          supabase.functions.invoke('create-ticket-notification', {
            body: {
              ticketId: variables.ticket_id,
              interactionType: 'message',
              actorUserId: user.id,
              data: {
                message: variables.message
              }
            }
          });
        } catch (notificationError) {
          console.error('Error creating notification:', notificationError);
          // Não falhar se a notificação falhar
        }
      }

      queryClient.invalidateQueries({ queryKey: ['support-ticket', variables.ticket_id] });
      queryClient.invalidateQueries({ queryKey: ['system-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-notification-count'] });
      toast({
        title: "Mensagem enviada",
        description: "Sua mensagem foi enviada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao enviar mensagem",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useSupportStats() {
  return useQuery({
    queryKey: ['support-stats'],
    queryFn: async () => {
      console.log('📊 Fetching support stats...');
      
      const [openTickets, inProgressTickets, resolvedTickets, totalTickets] = await Promise.all([
        supabase.from('support_tickets').select('id', { count: 'exact' }).eq('status', 'open'),
        supabase.from('support_tickets').select('id', { count: 'exact' }).eq('status', 'in_progress'),
        supabase.from('support_tickets').select('id', { count: 'exact' }).eq('status', 'resolved'),
        supabase.from('support_tickets').select('id', { count: 'exact' }),
      ]);

      const stats = {
        open: openTickets.count || 0,
        inProgress: inProgressTickets.count || 0,
        resolved: resolvedTickets.count || 0,
        total: totalTickets.count || 0,
      };

      console.log('📊 Support stats:', stats);
      console.log('🔍 Individual queries results:', {
        openTickets: openTickets.count,
        openError: openTickets.error,
        inProgressTickets: inProgressTickets.count,
        inProgressError: inProgressTickets.error,
        resolvedTickets: resolvedTickets.count,
        resolvedError: resolvedTickets.error,
        totalTickets: totalTickets.count,
        totalError: totalTickets.error,
      });

      return stats;
    },
  });
}